import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RolesPermissions() {
  const [meta, setMeta] = useState({ modules: [], actions: [], roles: [] });
  const [allPerms, setAllPerms] = useState({});
  const [role, setRole] = useState("");
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState({ label: "" });

  const reloadMeta = async () => {
    const [m, p] = await Promise.all([api.get("/meta/rbac"), api.get("/roles/permissions")]);
    setMeta(m.data);
    setAllPerms(p.data);
    return m.data;
  };

  const createRole = async () => {
    if (!newRole.label || newRole.label.length < 2) { toast.error("Enter a role name"); return; }
    const key = newRole.label.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    try {
      await api.post("/roles/custom", { key, label: newRole.label.trim() });
      toast.success("Custom role created");
      setNewRoleOpen(false); setNewRole({ label: "" });
      await reloadMeta(); changeRoleTo(key);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const deleteRole = async () => {
    if (!window.confirm(`Delete custom role "${roleLabel}"?`)) return;
    try {
      await api.delete(`/roles/custom/${role}`);
      toast.success("Role deleted");
      const m = await reloadMeta();
      changeRoleTo(m.roles.find((r) => r.key !== "admin")?.key);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([api.get("/meta/rbac"), api.get("/roles/permissions")]);
        setMeta(m.data);
        setAllPerms(p.data);
        const firstNonAdmin = m.data.roles.find((r) => r.key !== "admin")?.key || m.data.roles[0]?.key;
        setRole(firstNonAdmin);
        setDraft(JSON.parse(JSON.stringify(p.data[firstNonAdmin] || {})));
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const changeRole = (r) => {
    setRole(r);
    setDraft(JSON.parse(JSON.stringify(allPerms[r] || {})));
  };
  const changeRoleTo = (r) => { if (r) changeRole(r); };

  const toggle = (moduleKey, action) => {
    setDraft((prev) => {
      const acts = new Set(prev[moduleKey] || []);
      acts.has(action) ? acts.delete(action) : acts.add(action);
      return { ...prev, [moduleKey]: Array.from(acts) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/roles/${role}/permissions`, { permissions: draft });
      setAllPerms((prev) => ({ ...prev, [role]: data.permissions }));
      toast.success("Permissions updated");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  const isAdmin = role === "admin";
  const currentRole = meta.roles.find((r) => r.key === role);
  const roleLabel = currentRole?.label || role;
  const isCustom = !!currentRole?.custom;

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <NavLink to="/users" className="mb-1 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to users</NavLink>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Roles &amp; Permissions</h2>
          <p className="mt-1 text-sm text-slate-500">Control which modules and actions each role can access.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <p className="mb-1 text-xs font-medium text-slate-600">Role</p>
            <Select value={role} onValueChange={changeRole}>
              <SelectTrigger className="w-56" data-testid="perm-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {meta.roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}{r.custom ? " (custom)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => setNewRoleOpen(true)} data-testid="new-role-btn"><Plus className="mr-2 h-4 w-4" />New role</Button>
          {isCustom && <Button variant="outline" className="text-rose-600" onClick={deleteRole} data-testid="delete-role-btn"><Trash2 className="mr-2 h-4 w-4" />Delete role</Button>}
          <Button onClick={save} disabled={saving || isAdmin} data-testid="save-permissions-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save
          </Button>
        </div>
      </div>

      <Dialog open={newRoleOpen} onOpenChange={setNewRoleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New custom role</DialogTitle><DialogDescription>Create a role, then set its module permissions. Users can be assigned to it.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label className="text-xs">Role name</Label><Input value={newRole.label} onChange={(e) => setNewRole({ label: e.target.value })} placeholder="e.g. Floor Supervisor" data-testid="new-role-name-input" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setNewRoleOpen(false)}>Cancel</Button><Button onClick={createRole} data-testid="create-role-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdmin && (
        <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="admin-perm-note">
          The Administrator role always has full access and cannot be restricted.
        </Card>
      )}

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="sticky left-0 bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Module</th>
              {meta.actions.map((a) => (
                <th key={a} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">{a}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {meta.modules.map((m) => (
              <tr key={m.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`perm-row-${m.key}`}>
                <td className="sticky left-0 bg-white px-4 py-3 font-medium text-slate-800">{m.label}</td>
                {meta.actions.map((a) => {
                  const checked = isAdmin ? true : (draft[m.key] || []).includes(a);
                  return (
                    <td key={a} className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <Checkbox checked={checked} disabled={isAdmin}
                          onCheckedChange={() => toggle(m.key, a)}
                          data-testid={`perm-${m.key}-${a}`} />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
